import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

interface LanguageSelectorProps {
  variant?: 'default' | 'compact';
  className?: string;
}

const LanguageSelector = ({ variant = 'default', className = '' }: LanguageSelectorProps) => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (languageCode: string) => {
    console.log('Changing language to:', languageCode);
    i18n.changeLanguage(languageCode);
    
    // Store language preference in localStorage for persistence
    localStorage.setItem('i18nextLng', languageCode);
  };

  const normalizedLanguage = i18n.resolvedLanguage?.split('-')[0] || i18n.language?.split('-')[0] || 'en';
  const currentLanguage = languages.find(lang => lang.code === normalizedLanguage) || languages[0];

  if (variant === 'compact') {
    return (
      <Select value={normalizedLanguage} onValueChange={handleLanguageChange}>
        <SelectTrigger 
          className={`w-auto bg-gray-700 text-gray-300 border-gray-600 ${className}`}
          aria-label="Select language"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm">{currentLanguage.name}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
          {languages.map((language) => (
            <SelectItem 
              key={language.code} 
              value={language.code}
              className="hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span>{language.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        <Globe className="inline h-4 w-4 mr-1" />
        {t('common.chooseLanguage')}
      </label>
      <Select value={normalizedLanguage} onValueChange={handleLanguageChange}>
        <SelectTrigger 
          className="w-full bg-white border border-gray-300"
          aria-label="Select language"
        >
          <SelectValue>
            <div className="flex items-center gap-2">
              <span>{currentLanguage.flag}</span>
              <span>{currentLanguage.name}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
          {languages.map((language) => (
            <SelectItem 
              key={language.code} 
              value={language.code}
              className="hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span>{language.flag}</span>
                <span>{language.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector;
